// DeltaSlider — the shared delta-bar slider React component.
//
// A native <input type="range"> whose track fill is painted between the
// default/zero position and the current value (a "delta-bar"), rather than
// emerging from the left like a bslib ionRangeSlider. The fill collapses at
// the default; above it the fill grows rightward, below it leftward.
//
// Used standalone for `scalar_multiplier` ("scale by ±%") groups, and inside
// CurveEditor as the control beneath the decoration chart. Both write a single
// numeric value to Shiny via setInputValue(inputId, …), so the server-side
// panel reactive at input[[group$id]] reads the same scalar the bslib slider
// produced — the engine boundary is unchanged.
//
// Accessibility: the native range input carries keyboard support and the
// "slider" role for free; we add aria-label (the bslib <label> gave this
// implicitly) and aria-valuetext (the signed, suffixed readout).

import { useCallback, useEffect, useMemo, useState } from "react";
import type { DeltaSliderProps } from "./types";
import { useResetAllSignal } from "../../state/reset_signal";
import { recordControl } from "../../state/share_store";
import type { ControlMeta } from "../../state/share_store";
import { notifyRestored, restoredNumber } from "../../state/share_restore";
import { deltaBarBackground, formatDelta } from "./delta_slider_logic";
import { InfoIcon } from "../info_icon";

export function DeltaSlider({
  inputId,
  label,
  instrument,
  description,
  defaultValue,
  min,
  max,
  step,
  postfix = "",
  onValueChange,
}: DeltaSliderProps): JSX.Element {
  const [value, setValue] = useState<number>(() =>
    restoredNumber(inputId, defaultValue, min, max),
  );
  const reset = useCallback(() => setValue(defaultValue), [defaultValue]);
  // Page-level "Reset all" reaches React islands via the document-delegated
  // click signal (this slider isn't a Shiny widget, so the server reset can't
  // reach it). #132.
  useResetAllSignal(reset);

  // Static display metadata for the "Your reform" modal (#65). The value is
  // itself the %-delta, so the modal shows the readout directly with no
  // separate baseline column.
  const meta = useMemo<ControlMeta>(
    () => ({ kind: "delta", label, instrument, default: defaultValue, postfix }),
    [label, instrument, defaultValue, postfix],
  );

  // Push the current value to the Shiny side on every change. "deferred"
  // debounces by Shiny's natural flush boundaries; the server reads the
  // latest value when the user clicks Simulate.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.Shiny?.setInputValue?.(inputId, value, { priority: "deferred" });
    // Mirror into the share registry so the Copy-link button can encode the
    // current reform (#244) and the reform modal can list it (#65).
    recordControl(inputId, value, value !== defaultValue, meta);
    // Report a restored control so the shared reform auto-runs once every
    // control has committed (#297). No-op unless this id was restored.
    notifyRestored(inputId);
  }, [inputId, value, defaultValue, meta]);

  // Mirror the value to an optional host (e.g. CurveEditor's chart). Kept
  // separate from the Shiny push so a host that doesn't pass it costs nothing.
  useEffect(() => {
    onValueChange?.(value);
  }, [onValueChange, value]);

  // Delta-bar track: paint the brand-primary fill between the default
  // position and the current value, leaving the rest neutral. At the
  // default the fill collapses and the slider reads "no change".
  const sliderStyle = useMemo<React.CSSProperties>(
    () => ({ background: deltaBarBackground(min, max, defaultValue, value) }),
    [value, defaultValue, min, max],
  );

  const sliderId = `${inputId}--slider`;
  const isModified = value !== defaultValue;
  const readout = formatDelta(value, postfix);

  return (
    <div className="beamm-delta-slider" data-testid={`delta-slider-${inputId}`}>
      <label
        htmlFor={sliderId}
        className="beamm-delta-slider__label beamm-input-island__label"
      >
        <span>{label}</span>
        {description ? <InfoIcon description={description} /> : null}
      </label>
      <div className="beamm-delta-slider__row">
        <input
          id={sliderId}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          className="beamm-delta-slider__input"
          data-testid={`delta-slider-input-${inputId}`}
          aria-label={label}
          aria-valuetext={readout}
          style={sliderStyle}
        />
        <span
          className="beamm-delta-slider__value"
          data-testid={`delta-slider-value-${inputId}`}
        >
          {readout}
        </span>
        {/*
          The reset slot is always present (fixed width) so the row's
          geometry — and therefore the slider thumb's pixel position —
          doesn't shift when the Reset button mounts/unmounts as the
          value crosses the default. The button itself is still only
          rendered when modified (the "no affordance at default" contract).
        */}
        <span className="beamm-delta-slider__reset-slot">
          {isModified ? (
            <button
              type="button"
              className="beamm-delta-slider__reset beamm-input-island__reset"
              data-testid={`delta-slider-reset-${inputId}`}
              onClick={reset}
            >
              Reset
            </button>
          ) : null}
        </span>
      </div>
    </div>
  );
}
